import { animate, state, style, transition, trigger } from '@angular/animations';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnInit,
  OnChanges,
  OnDestroy,
  Output,
  ViewChild,
  ViewChildren,
  QueryList,
  SimpleChanges,
  inject,
  Injector,
} from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl } from '@angular/forms';
import { MatSort, Sort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { Actions, ofActionSuccessful, select, Store } from '@ngxs/store';
import { debounceTime, distinctUntilChanged, map, Subscription } from 'rxjs';
import { MainTableFilterModulesEnum } from 'src/app/core/enums/main-table-filter-modules.enum';
import { MainTableFilterIdentification } from 'src/app/core/models/main-table-filter-identification.model';
import { MOBILE_LAYOUT_QUERIES } from 'src/app/shared/constants/responsive-breakpoints';
import { AdminDocumentsRoutes } from 'src/app/core/enums/routes.enum';
import { MainTableLogicService } from 'src/app/core/services/main-table-logic.service';
import { defaultInitialPageSize } from 'src/app/core/table-config/table-paging-defaults';
import { SelectionModel } from '@angular/cdk/collections';
import {
  LoadRunTimeChildrenForTable,
  ClearRunTimeChildrenForTable,
} from 'src/app/run-time/state/run-time.actions';
import { RunTimeState } from 'src/app/run-time/state/run-time.state';
import { MainTableCustomSortingService } from '../../../core/services/main-table-custom-sorting.service';
import {
  ApplyColumnFilters,
  ApplySorting,
  ClearAllSavedFilters,
  ClearFilterDetails,
  ClearModuleColumnFilters,
  ReFetchAndClearSorting,
  ResetFilterSearch,
  ResetFilterSearchTerm,
} from '../../../core/state/main-table-filter.actions';
import { ResizedEvent } from '../../directives/resize.directive';
import { FitToViewportDirective } from '../../directives/fit-to-viewport.directive';
import { MainTableModuleNamesEnum } from '../../enums/main-table-module-names.enum';
import { MainTableFilterState } from '../../../core/state/main-table-filter.state';
import { TableFilterStorageService } from '../../../core/services/table-filter-storage.service';
import {
  MainTableFilterModel,
  MainTableFilterSearchByModel,
  MainTableSortingType,
} from '../../models/main-table-filter.model';
import {
  MainTableColumnFilterComponent,
  MainTableFilterColumnData,
} from './main-table-column-filter/main-table-column-filter.component';
import { PanelConfig } from './models/panel.model';
import {
  MainTableMobileFiltersComponent,
  MainTableMobileFiltersResult,
} from './main-table-mobile-filters/main-table-mobile-filters.component';
import { TableRotationHintService } from '../services/table-rotation-hint.service';

const CHILD_RUNTIME_COLUMN = 'dropdownForRunTimeChildren__hidden';
const PAUSE_HISTORY_EXPAND_COLUMN = 'pauseHistoryExpand__hidden';
const FULLSCREEN_GESTURE_AXIS_THRESHOLD = 6;
const FULLSCREEN_SCROLL_EPSILON = 1;
const FULLSCREEN_SCROLL_ROOM_BUFFER = 2;
/** Wheel/trackpad idle gap that ends a gesture (stall reset + handoff cleanup). */
const WHEEL_GESTURE_IDLE_MS = 150;
/**
 * Sustained chrome-out stalls before forcing fullscreen while page chrome remains.
 * Two stalls alone are often handoff layout lag, not a true exhausted parent.
 */
const FULLSCREEN_CHROME_OUT_STALL_FORCE = 8;
const WHEEL_LINE_HEIGHT_PX = 16;
const MESSAGE_RECIPIENTS_EXPAND_COLUMN = 'messageRecipientsExpand__hidden';
const MAINTENANCE_SUBHEADER_COLUMNS = ['maintenanceJobsGroupName', 'maintenanceJobsGroupRemainder'];
const MAINTENANCE_SUBHEADER_COLUMNS_WITH_SELECTOR = [
  'multiselect',
  ...MAINTENANCE_SUBHEADER_COLUMNS,
];
const EXPANDABLE_COLUMNS = [
  'expand',
  CHILD_RUNTIME_COLUMN,
  PAUSE_HISTORY_EXPAND_COLUMN,
  MESSAGE_RECIPIENTS_EXPAND_COLUMN,
];

@Component({
  selector: 'app-main-table',
  templateUrl: './main-table.component.html',
  styleUrls: ['./main-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('detailExpand', [
      state('collapsed,void', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
  exportAs: 'appMainTable',
  standalone: false,
})
export class MainTableComponent implements OnChanges, OnInit, AfterViewInit, OnDestroy {
  private readonly injector = inject(Injector);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly hostElement = inject(ElementRef<HTMLElement>);
  private readonly dialog = inject(MatDialog);
  private readonly tableRotationHintService = inject(TableRotationHintService);
  readonly isMobileTableView = toSignal(
    inject(BreakpointObserver)
      .observe(MOBILE_LAYOUT_QUERIES)
      .pipe(map((result) => result.matches)),
    { initialValue: MOBILE_LAYOUT_QUERIES.some((query) => window.matchMedia(query).matches) }
  );

  // This is a generic table component
  // You are obligated to specify only two properties
  // the "tableData" and "displayedColumns"
  // You can also specify optional parameters like
  // "pageSizeOptions", but it can be left as default
  @Input() tableData: any;
  @Input() displayedColumns: string[];
  @Input() detailsDisplayedColumns: string[];
  @Input() expandableRowDataField = '';
  @Input() expandableRowNestedTable = false;
  @Input() expandableRowEmptyLabel = 'thereIsCurrentlyNoDataInTheTable';
  @Input() expandOnRowClick = true;
  @Input() clickableColumns: string[] = [];
  /** Translation keys per column for hover tooltips on body cells */
  @Input() columnHoverTooltips: Record<string, string> = {};
  @Input() disabledColumnHeaders: string[] = [];
  @Input() customSortingColumns: string[] = [];
  @Input() filterConfig: Record<string, MainTableFilterColumnData> | null = null;
  // eslint-disable-next-line no-magic-numbers
  @Input() totalPages: any;

  // additional options

  // Option 1:
  // urlForRow as string
  @Input() urlForRow = '';
  @Input() trClass = '';
  @Input() searchingEnabled = true;
  @Input() filteringEnabled = true;
  @Input() sortingEnabled = true;
  @Input() paginationEnabled = true;
  @Input() elevationEnabled = true;
  @Input() showHeaders = true;
  @Input() showPageSizeSelector = true;
  @Input() defaultInitialPageSize = defaultInitialPageSize;
  @Input() viewMode: 'full' | 'embedded' = 'full';
  @Input() moduleNameForPaging: MainTableFilterModulesEnum;
  @Input() idFieldNameForRowHandler: string;
  @Input() trClassMap: Record<string, string> = {};

  // Option 2:
  // hasRoutingForRow is boolean for not using a routing for a row
  @Input() hasRoutingForRow = true;
  @Input() rowClickDisabled = false;
  @Input() filterPredicate: (data: any, filter: string) => boolean;
  @Input() specificTemplateName: string;
  @Input() buttonFunctions: { [key: string]: (i: number) => (...args: any[]) => void } = {};
  @Input() hideButtons = true;
  @Input() additional: any; // TODO Illia: additional is handled in multiple places, should it be just here?
  @Input() @HostBinding('class.hide-main-table-show-search') loading = false;
  @Input() initialSelection: any[] = [];
  /** Controls a configured multiselect column on mobile; desktop selectors remain visible.
   *  On mobile, checkboxes stay mounted and are hidden with CSS when this is false. */
  @Input() selectionMode = true;
  @Input() panelConfig: PanelConfig[];
  @Input() disableSelectionByColumn = '';
  @Input() hideDisabledCheckbox = false;
  @Input() disabledCheckboxIcon = '';
  @Input() disabledCheckboxIconTooltip = '';
  @Input() columnTitlesMap: Record<string, string> = {};
  @Input() skipInitialLoad = false;
  @Input() fitToViewport = false;
  /** When true with fitToViewport, the table always fills remaining viewport height. */
  @Input() fillAvailableViewport = false;
  @Input() initialFilters: MainTableFilterSearchByModel[] = [];
  /** Enables the horizontally scrollable mobile table layout. */
  @Input() mobileOptimized = true;
  /** Lets the page own vertical scrolling while the table viewport contains horizontal overflow. */
  @Input() @HostBinding('class.embedded-in-page-scroll') embeddedInPageScroll = false;
  /** Puts search and projected actions on one compact mobile row instead of stacked full-width rows. */
  @Input() @HostBinding('class.compact-mobile-actions') compactMobileActions = false;
  /** Applies the standard mobile search/filter toolbar styling when filters are projected by the page. */
  @Input() mobileFilterToolbar = false;
  /** Uses the full-screen mobile filter flow when column filters are configured. */
  @Input() mobileFilters = true;
  /** Lets the table cover the mobile page content once the table scrolls vertically. */
  @Input() mobileFullscreenOnScroll = false;
  /** Offset reserved above the mobile fullscreen table, normally the mobile top toolbar height. */
  @Input() mobileFullscreenTopOffset = '2.5rem';
  /**
   * Optional sticky page chrome (e.g. `.form-page-header`) that must stay above the fullscreen table.
   * When set and connected, its `getBoundingClientRect().bottom` replaces `mobileFullscreenTopOffset`.
   */
  @Input() mobileFullscreenTopBoundary: HTMLElement | null = null;
  /** Keeps the header visible while the mobile table container scrolls vertically. */
  @Input() stickyHeader = true;
  /** Defaults to the first displayed column. Pass an empty array to disable sticky columns. */
  @Input() stickyColumns?: string[];
  /** Columns that intentionally use a narrow mobile layout, such as a status-dot column. */
  @Input() compactMobileColumns: string[] = [];
  /** Optional mobile-only widths keyed by column name. */
  @Input() mobileColumnWidths: Record<string, string> = {};
  @Input() tableMinWidth = '52rem';
  @Output() readonly rowHandler: EventEmitter<string | any> = new EventEmitter<string | any>();
  @Output() readonly columnHandler = new EventEmitter<{ column: string; row: any }>();
  @Output() readonly searchEmitter = new EventEmitter<string>();
  @Output() readonly selectionEmitter = new EventEmitter<any>();
  @Output() readonly customSortingEmitter = new EventEmitter<{
    column: string;
    direction: string;
  }>();
  @Output() readonly columnFiltersEmitter = new EventEmitter<
    Record<string, MainTableFilterSearchByModel[]>
  >();

  @ViewChild(MatSort) sort: MatSort;
  @ViewChild('tableContainer') tableContainer?: ElementRef<HTMLElement>;
  @ViewChild(FitToViewportDirective) fitToViewportDirective?: FitToViewportDirective;
  @ViewChildren(MainTableColumnFilterComponent)
  filterComponents: QueryList<MainTableColumnFilterComponent>;

  runTimeChildElements = select(RunTimeState.getRunTimeChildrenForTable);

  dataSource: MatTableDataSource<any>;
  multipleSelection = new SelectionModel<any>(true, []);
  showSelector = false;

  expandedElement: any | null;

  filterValue: string;
  sortDirection: MainTableSortingType = '';
  orderByColumn = '';
  columnFilters: Record<string, MainTableFilterSearchByModel[]> = {};
  tableColor: string;
  // Remember to add a new enum for new template on shared main-table component
  moduleRouteEnums = MainTableModuleNamesEnum;
  moduleName: string;

  currentUrl: string;
  isExpandable = false;
  needExpandableRowLoader = false;
  readonly searchControl = new FormControl('', { nonNullable: true });
  subheaderContentWidth = 0;
  columnDefinitions: string[] = [];
  renderedColumns: string[] = [];
  mobileRenderedColumns: string[] = [];
  mobileStickyColumns: string[] = [];
  subheaderColumns = ['subHeader', 'subHeaderRemainder'];
  subheaderRemainderColspan = 1;
  columnPanelMap: Record<string, PanelConfig> = {};
  expandableRowLoadingActions = [LoadRunTimeChildrenForTable];

  get hasActiveFilters(): boolean {
    return (
      this.columnFilters && Object.values(this.columnFilters).some((filters) => filters.length > 0)
    );
  }

  get hasTransientFilters(): boolean {
    return this.initialFilters && this.initialFilters.length > 0;
  }

  get activeFilterCount(): number {
    return Object.values(this.columnFilters).filter((filters) => filters.length > 0).length;
  }

  get mobileTableActive(): boolean {
    return this.mobileOptimized && this.isMobileTableView();
  }

  get selectionModeActive(): boolean {
    return !this.mobileTableActive || this.selectionMode;
  }

  get maintenanceSubheaderColumns(): string[] {
    return this.showSelector
      ? MAINTENANCE_SUBHEADER_COLUMNS_WITH_SELECTOR
      : MAINTENANCE_SUBHEADER_COLUMNS;
  }

  get maintenanceSubheaderRemainderColspan(): number {
    return Math.max(this.renderedColumns.length - (this.showSelector ? 2 : 1), 1);
  }

  get tableRenderedColumns(): string[] {
    return this.mobileTableActive ? this.mobileRenderedColumns : this.renderedColumns;
  }

  get mobileFiltersActive(): boolean {
    return (
      this.mobileFilters &&
      this.filteringEnabled &&
      !!this.filterConfig &&
      this.displayedColumns?.some((column) => !!this.filterConfig?.[column]) === true &&
      this.isMobileTableView()
    );
  }

  get mobileFilterToolbarActive(): boolean {
    return this.mobileFiltersActive || (this.mobileFilterToolbar && this.isMobileTableView());
  }

  private subscription = new Subscription();
  private filterStateSubscription?: Subscription;
  private lastEmittedSearchTerm = '';
  private isApplyingSavedSort = false;
  private fullscreenGestureAxis: 'horizontal' | 'vertical' | null = null;
  private touchStartX = 0;
  private touchStartY = 0;
  private touchLastY = 0;
  private mobileFullscreenHandoffActive = false;
  private mobileFullscreenScrollRoom = 0;
  private mobileFullscreenTopOffsetPx?: number;
  private mobileFullscreenViewportHeightPx?: number;
  private mobileFullscreenMeasuredTopOffsetPx?: number;
  private mobileFullscreenMeasuredViewportHeightPx?: number;
  private mobileFullscreenTopBoundaryObserver?: ResizeObserver;
  private mobileFullscreenTopBoundaryViewportCleanup?: () => void;
  private mobileFullscreenTopBoundaryMeasureFrame?: number;
  private mobileFullscreenTopOffsetRefreshFrame?: number;
  private mobileFullscreenHandoffOverflowRevalidationFrame?: number;
  private mobileFullscreenChromeOutStallCount = 0;
  /** True if the table overflowed when handoff started (survives grow-only fit updates). */
  private mobileFullscreenHandoffHadOverflow = false;
  private mobileFullscreenLastWheelAt = 0;
  private mobileFullscreenWheelIdleTimer?: ReturnType<typeof setTimeout>;
  private mobileFullscreenGestureScrollParent?: HTMLElement | null;
  private viewInitialized = false;

  @HostBinding('class.mobile-table-vertically-scrolled')
  mobileTableVerticallyScrolled = false;

  @HostBinding('class.mobile-table-horizontally-scrolled')
  mobileTableHorizontallyScrolled = false;

  @HostBinding('class.mobile-table-fullscreen')
  get mobileTableFullscreenClass(): boolean {
    return this.mobileFullscreenActive && this.mobileFullscreenOnScrollActive;
  }

  @HostBinding('class.mobile-table-fullscreen-handoff')
  get mobileTableFullscreenHandoffClass(): boolean {
    return this.mobileFullscreenHandoffActive && this.mobileFullscreenOnScrollActive;
  }

  /**
   * Suspend automatic fit-to-viewport while handoff/fullscreen is active.
   * Handoff still updates height via forced grow-only recalculation so content
   * stays visible without the unset/shrink jump.
   */
  get mobileFullscreenFitSuspended(): boolean {
    return (
      this.mobileFullscreenOnScrollActive &&
      (this.mobileFullscreenActive || this.mobileFullscreenHandoffActive)
    );
  }

  @HostBinding('style.--main-table-mobile-fullscreen-top')
  get mobileTableFullscreenTopOffsetStyle(): string {
    const topOffset = this.getMobileFullscreenTopOffsetStylePx();
    return topOffset === undefined ? this.mobileFullscreenTopOffset : `${topOffset}px`;
  }

  @HostBinding('style.--main-table-mobile-fullscreen-scroll-room')
  get mobileTableFullscreenScrollRoomStyle(): string {
    return `${this.mobileFullscreenScrollRoom}px`;
  }

  @HostBinding('style.--main-table-mobile-fullscreen-height')
  get mobileTableFullscreenHeightStyle(): string | null {
    const viewportHeight = this.getMobileFullscreenViewportHeightStylePx();
    const topOffset = this.getMobileFullscreenTopOffsetStylePx();
    if (viewportHeight === undefined || topOffset === undefined) {
      return null;
    }

    return `${Math.max(viewportHeight - topOffset, 0)}px`;
  }

  private mobileFullscreenActive = false;

  constructor(
    private store: Store,
    private actions$: Actions,
    private router: Router,
    private mainTableLogicService: MainTableLogicService,
    private mainTableCustomSortingService: MainTableCustomSortingService,
    private tableFilterStorageService: TableFilterStorageService
  ) {
    const { url } = this.router;
    this.currentUrl = url;
    if (url === '/' || url === '') {
      this.moduleName = MainTableModuleNamesEnum.MainDashboard;
    }
    const urlParts = url.split(/[?\/]/g);
    urlParts.forEach((urlFragment: string) => {
      if ((<any>Object).values(this.moduleRouteEnums).includes(urlFragment)) {
        this.moduleName = urlFragment;
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    const dataSourceChanged = !!changes.tableData || !this.dataSource;
    if (dataSourceChanged) {
      this.dataSource = new MatTableDataSource(this.tableData ? this.tableData : []);
      this.setSortAndPagination();
      this.setCustomFilterPredicate();
      this.scheduleMobileFullscreenHandoffOverflowRevalidation();
    }
    if (dataSourceChanged || changes.initialSelection) {
      this.setInitiallySelectedRows();
    }
    if (!dataSourceChanged && changes.sortingEnabled) {
      this.setSortAndPagination();
    }
    if (!dataSourceChanged && changes.filterPredicate) {
      this.setCustomFilterPredicate();
    }
    if (changes.displayedColumns) {
      this.setRenderedColumns();
    }
    if (changes.selectionMode?.previousValue && !changes.selectionMode.currentValue) {
      this.clearSelection();
    }
    if (changes.mobileFullscreenOnScroll && !changes.mobileFullscreenOnScroll.currentValue) {
      this.setMobileFullscreenActive(false);
      this.clearMobileFullscreenHandoff();
    }
    if (changes.mobileFullscreenOnScroll || changes.mobileFullscreenTopBoundary) {
      this.syncMobileFullscreenTopBoundaryObservers();
    }
    if (changes.displayedColumns || changes.selectionMode || changes.stickyColumns) {
      this.setMobileStickyColumns();
    }
    if (changes.panelConfig) {
      this.displayedColumns.forEach((column, index) => {
        const panel = this.panelConfig.find(
          (p) => p.startIndex <= index && p.endIndex >= index
        ) as PanelConfig;
        this.columnPanelMap[column] = { ...panel };
      });
    }
  }

  ngOnInit(): void {
    this.setInitiallySelectedRows();
    this.subscription.add(
      toObservable(this.isMobileTableView, { injector: this.injector })
        .pipe(distinctUntilChanged())
        .subscribe((isMobileTableView) => {
          this.syncMobileFullscreenTopBoundaryObservers();
          if (isMobileTableView) {
            return;
          }
          this.setMobileFullscreenActive(false);
          this.clearMobileFullscreenHandoff();
          this.fullscreenGestureAxis = null;
        })
    );
  }

  ngAfterViewInit(): void {
    this.viewInitialized = true;
    this.tableRotationHintService.showIfNeeded();
    this.setSortAndPagination();
    this.applyFilter();
    this.applySavedSorting();
    this.isExpandable = EXPANDABLE_COLUMNS.some((col) => this.displayedColumns?.includes(col));
    this.needExpandableRowLoader = this.displayedColumns?.includes(CHILD_RUNTIME_COLUMN);
    this.populateColumnFiltersFromInitial();
    this.syncMobileFullscreenTopBoundaryObservers();
    this.subscription.add(
      this.actions$.pipe(ofActionSuccessful(ClearAllSavedFilters)).subscribe(() => {
        this.resetLocalFilterState();
        this.store.dispatch([
          new ReFetchAndClearSorting(this.moduleNameForPaging, this.additional),
          new ResetFilterSearch(this.moduleNameForPaging, this.additional),
          new ResetFilterSearchTerm(this.moduleNameForPaging, this.additional),
        ]);
      })
    );
    this.subscription.add(
      this.actions$
        .pipe(ofActionSuccessful(ClearModuleColumnFilters))
        .subscribe(({ module, column }) => this.clearLocalColumnFilters(module, column))
    );
  }

  private populateColumnFiltersFromInitial(): void {
    if (!this.initialFilters || this.initialFilters.length === 0) {
      return;
    }
    this.columnFilters = this.groupFiltersByColumn(this.initialFilters);
  }

  setInitiallySelectedRows(): void {
    this.multipleSelection.setSelection(
      ...(this.tableData?.filter((u: any) =>
        this.initialSelection.find(
          (s) => s[this.idFieldNameForRowHandler] === u[this.idFieldNameForRowHandler]
        )
      ) || [])
    );
  }

  ngOnDestroy(): void {
    this.viewInitialized = false;
    this.setMobileFullscreenActive(false);
    this.clearMobileFullscreenHandoff();
    this.clearMobileFullscreenWheelIdleTimer();
    this.teardownMobileFullscreenTopBoundaryObservers();
    this.cancelMobileFullscreenActiveTopOffsetRefresh();
    this.cancelMobileFullscreenHandoffOverflowRevalidation();
    this.tableRotationHintService.release();
    this.subscription.unsubscribe();
  }

  applyFilter(): void {
    this.filterStateSubscription?.unsubscribe();
    const filterIdentification = new MainTableFilterIdentification(
      this.moduleNameForPaging,
      this.additional
    );
    const filterState = this.store.selectSignal(
      MainTableFilterState.getPagingDetails(filterIdentification)
    );

    if (this.searchingEnabled) {
      this.subscription.add(
        this.searchControl.valueChanges
          .pipe(
            debounceTime(500),
            map(() => this.searchControl.value ?? '')
          )
          .subscribe((value) => this.emitSearchTerm(value))
      );
    }

    this.filterStateSubscription = toObservable(filterState, { injector: this.injector }).subscribe(
      (currentFilter) => {
        this.orderByColumn = currentFilter?.orderBy || '';
        this.sortDirection = currentFilter?.ascDesc || '';
        this.replaceColumnFilters(currentFilter?.searchBy);
        this.syncSearchControl(currentFilter?.searchTerm || '');
        this.syncSortHeader(currentFilter);
        this.cdr.markForCheck();
      }
    );
    this.subscription.add(this.filterStateSubscription);
  }

  applyColumnFilters(column: string, filters: MainTableFilterSearchByModel[]): void {
    this.columnFilters[column] = filters;
    if (!this.moduleNameForPaging) {
      this.columnFiltersEmitter.emit(this.columnFilters);
      return;
    }

    this.store.dispatch(
      new ApplyColumnFilters(
        this.moduleNameForPaging,
        Object.values(this.columnFilters).flat(),
        this.additional
      )
    );
    this.columnFiltersEmitter.emit(this.columnFilters);
  }

  clearFilters(): void {
    if (!this.moduleNameForPaging) {
      this.resetLocalFilterState();
      this.customSortingEmitter.emit({ column: '', direction: '' });
      return;
    }

    // Clear saved filters from localStorage for this module
    this.tableFilterStorageService.clearModuleFilters(this.moduleNameForPaging);
    this.store.dispatch(new ClearFilterDetails()).subscribe(() => {
      // Clear the controls after NGXS so the previous search term cannot be restored locally.
      this.resetLocalFilterState();
      this.store.dispatch([
        new ReFetchAndClearSorting(this.moduleNameForPaging, this.additional),
        new ResetFilterSearch(this.moduleNameForPaging, this.additional),
        new ResetFilterSearchTerm(this.moduleNameForPaging, this.additional),
      ]);
    });
  }

  openMobileFilters(): void {
    if (!this.filterConfig) {
      return;
    }

    const activeSortColumn = this.renderedColumns.find(
      (column) =>
        this.getProperHeaderNameForAPI(column, this.moduleNameForPaging).toLowerCase() ===
        this.orderByColumn.toLowerCase()
    );
    const dialogRef = this.dialog.open(MainTableMobileFiltersComponent, {
      data: {
        columns: this.renderedColumns,
        filterConfig: this.filterConfig,
        columnFilters: this.columnFilters,
        disabledColumnHeaders: this.disabledColumnHeaders,
        columnTitlesMap: this.columnTitlesMap,
        orderByColumn: activeSortColumn ?? '',
        sortDirection: this.sortDirection,
        sortingEnabled: this.sortingEnabled,
      },
      width: '100vw',
      height: '100dvh',
      maxWidth: 'none',
      panelClass: 'main-table-mobile-filters-dialog',
    });

    this.subscription.add(
      dialogRef.afterClosed().subscribe((result: MainTableMobileFiltersResult | undefined) => {
        if (!result) {
          return;
        }

        if (result.clearAll) {
          this.clearFilters();
          return;
        }

        if (result.sort) {
          this.sortData(result.sort);
        }

        if (result.column && result.filters) {
          this.applyColumnFilters(result.column, result.filters);
        }
      })
    );
  }

  isLastMobileStickyColumn(column: string): boolean {
    return this.mobileStickyColumns.at(-1) === column;
  }

  /** Clears the table search input (e.g. when a parent clears filters). */
  clearSearchField(): void {
    if (this.searchingEnabled) {
      this.searchControl.setValue('', { emitEvent: false });
      this.lastEmittedSearchTerm = '';
      this.cdr.markForCheck();
    }
  }

  private emitSearchTerm(value: string): void {
    if (value === this.lastEmittedSearchTerm) {
      return;
    }

    this.lastEmittedSearchTerm = value;
    this.searchEmitter.emit(value);
  }

  private clearLocalColumnFilters(module: MainTableFilterModulesEnum, column: string): void {
    if (module !== this.moduleNameForPaging || !this.columnFilters[column]) {
      return;
    }

    delete this.columnFilters[column];
    this.columnFiltersEmitter.emit(this.columnFilters);
    this.cdr.markForCheck();
  }

  private resetLocalFilterState(): void {
    this.sortDirection = '';
    this.orderByColumn = '';
    this.filterComponents?.forEach((f) => f.resetAllControls());
    this.columnFilters = {};
    this.clearSearchField();
    if (this.sort) {
      // Set flag to prevent API request
      this.isApplyingSavedSort = true;
      this.sort.active = '';
      this.sort.direction = '';
      this.sort.sortChange.emit({
        active: '',
        direction: '',
      });
      this.isApplyingSavedSort = false;
    }
    this.columnFiltersEmitter.emit(this.columnFilters);
  }

  private setMobileStickyColumns(): void {
    const hasMultiselect = this.renderedColumns.includes('multiselect');
    const contentColumns = this.renderedColumns.filter((column) => column !== 'multiselect');
    const configuredColumns = this.stickyColumns ?? contentColumns.slice(0, 1);
    const stickyColumnsDisabled = this.stickyColumns?.length === 0;
    const leadingColumnNames = new Set(stickyColumnsDisabled ? [] : configuredColumns);

    // Keep multiselect mounted and leading so CSS can hide it without rebuilding columns.
    if (hasMultiselect && !stickyColumnsDisabled) {
      leadingColumnNames.add('multiselect');
    }

    const leadingColumns = [
      ...(hasMultiselect && leadingColumnNames.has('multiselect') ? ['multiselect'] : []),
      ...this.renderedColumns.filter(
        (column) => column !== 'multiselect' && leadingColumnNames.has(column)
      ),
    ];
    const nextMobileRenderedColumns = [
      ...leadingColumns,
      ...this.renderedColumns.filter((column) => !leadingColumnNames.has(column)),
    ];
    if (!this.areColumnListsEqual(this.mobileRenderedColumns, nextMobileRenderedColumns)) {
      this.mobileRenderedColumns = nextMobileRenderedColumns;
    }

    // Sticky binding only — multiselect becomes sticky while selection mode is active.
    const stickyColumnNames = new Set(stickyColumnsDisabled ? [] : configuredColumns);
    if (this.selectionMode && hasMultiselect && !stickyColumnsDisabled) {
      stickyColumnNames.add('multiselect');
    }
    this.mobileStickyColumns = this.renderedColumns.filter((column) =>
      stickyColumnNames.has(column)
    );
  }

  private areColumnListsEqual(left: string[], right: string[]): boolean {
    return left.length === right.length && left.every((column, index) => column === right[index]);
  }

  private setRenderedColumns(): void {
    const displayedColumns = this.displayedColumns ?? [];
    this.renderedColumns = [...displayedColumns];
    this.columnDefinitions = this.renderedColumns.filter((column) => column !== 'multiselect');
    this.showSelector = this.renderedColumns.includes('multiselect');
    this.subheaderRemainderColspan = Math.max(this.renderedColumns.length - 1, 1);
  }

  sortData(sort: Sort) {
    // Skip API request if we're programmatically applying saved sort state
    if (this.isApplyingSavedSort) {
      return;
    }

    let sortDirection: MainTableSortingType = '';
    if (sort.direction === 'asc') {
      sortDirection = 'Ascending';
    } else if (sort.direction === 'desc') {
      sortDirection = 'Descending';
    } else if (sort.direction === '') {
      sortDirection = '';
    }
    this.sortDirection = sortDirection;
    if (this.customSortingColumns.includes(sort.active)) {
      this.orderByColumn = sort.direction
        ? this.getProperHeaderNameForAPI(sort.active, this.moduleNameForPaging)
        : '';
      this.customSortingEmitter.emit({ column: sort.active, direction: this.sortDirection });
      this.cdr.markForCheck();
      return;
    }
    const headerName = this.getProperHeaderNameForAPI(sort.active, this.moduleNameForPaging);
    this.store.dispatch(
      new ApplySorting(this.moduleNameForPaging, headerName, sortDirection, this.additional)
    );
  }

  handleRowClick(row: any) {
    if (this.rowClickDisabled) {
      return;
    }
    if (this.urlForRow === AdminDocumentsRoutes.Structure) {
      this.router.navigate([`${this.urlForRow}/${row.id}`]);
    } else if ((!row.count || row.count === 0) && this.hasRoutingForRow) {
      if (!row.count || row.count === 0) {
        let idForRouting = '';
        if (this.idFieldNameForRowHandler) {
          const rowElement = row[this.idFieldNameForRowHandler];
          idForRouting = rowElement instanceof AbstractControl ? rowElement.value : rowElement;
        } else if (row.id) {
          idForRouting = row.id;
        }
        if (this.rowHandler.observers.length > 0) {
          this.rowHandler.emit(idForRouting);
        } else if (this.urlForRow) {
          this.router.navigate([`${this.urlForRow}/${idForRouting}`]);
        }
      }
    } else if (!this.hasRoutingForRow) {
      // return whole row object if routing is disabled to handle click manually
      this.rowHandler.emit(row);
    }
    if (this.expandOnRowClick && row?.expandable !== false) {
      this.expandedElement = this.expandedElement === row ? null : row;
    }
  }

  handleColumnClick(event: MouseEvent, column: string, row: any) {
    if (this.clickableColumns.includes(column)) {
      event.stopPropagation();
      if (column === CHILD_RUNTIME_COLUMN) {
        const filterForRunTimeChildren = new MainTableFilterModel(
          MainTableFilterModulesEnum.RunTime
        );
        filterForRunTimeChildren.pageIndex = 0;
        // eslint-disable-next-line no-magic-numbers
        filterForRunTimeChildren.pageSize = 10000;
        this.store.dispatch(new ClearRunTimeChildrenForTable());
        this.store.dispatch(new LoadRunTimeChildrenForTable(row.id, filterForRunTimeChildren));
        this.expandedElement = this.expandedElement === row ? null : row;
      } else if (
        column === PAUSE_HISTORY_EXPAND_COLUMN ||
        (column === 'expand' && row.historyVersionRows?.length) ||
        (column === MESSAGE_RECIPIENTS_EXPAND_COLUMN && row.recipientDetailRows?.length)
      ) {
        this.expandedElement = this.expandedElement === row ? null : row;
      } else {
        this.columnHandler.emit({ column, row });
      }
    }
  }

  handleChildColumnClick(event: { event: MouseEvent; column: string; row: any }) {
    if (this.clickableColumns.includes(event.column)) {
      event.event.stopPropagation();
      const { column } = event;
      const { row } = event;
      this.columnHandler.emit({ column, row });
    }
  }

  isExpired(expiryDate: string): boolean {
    if (this.mainTableLogicService.isExpired(expiryDate)) {
      this.tableColor = 'salmon';
      return true;
    }
    if (this.mainTableLogicService.isSoonExpired(expiryDate)) {
      this.tableColor = 'lightyellow';
      return true;
    }

    return this.mainTableLogicService.isExpired(expiryDate);
  }

  isColumnHeaderDisabled(columnHeaderName: string): boolean {
    return this.disabledColumnHeaders.some((header: string) => header === columnHeaderName);
  }

  onResize(event: ResizedEvent): void {
    if (event.newRect.width > 0) {
      this.subheaderContentWidth = event.newRect.width;
    }
  }

  onTableTouchStart(event: TouchEvent, _container: HTMLElement): void {
    this.clearMobileFullscreenGestureScrollParent();
    if (!this.mobileFullscreenOnScrollActive || event.touches.length !== 1) {
      this.fullscreenGestureAxis = null;
      return;
    }

    const touch = event.touches[0];
    this.fullscreenGestureAxis = null;
    this.touchStartX = touch.clientX;
    this.touchStartY = touch.clientY;
    this.touchLastY = touch.clientY;

    // Defer fullscreen handoff until vertical intent is clear so the first
    // horizontal pan is not competing with chrome-scroll preparation.
    this.mobileFullscreenChromeOutStallCount = 0;
  }

  onTableScroll(container: HTMLElement): void {
    const isVerticallyScrolled = this.mobileTableActive && container.scrollTop > 0;
    const isHorizontallyScrolled = this.mobileTableActive && container.scrollLeft > 0;
    if (
      this.mobileTableVerticallyScrolled === isVerticallyScrolled &&
      this.mobileTableHorizontallyScrolled === isHorizontallyScrolled
    ) {
      return;
    }

    this.mobileTableVerticallyScrolled = isVerticallyScrolled;
    this.mobileTableHorizontallyScrolled = isHorizontallyScrolled;
    this.cdr.markForCheck();
  }

  onTableWheel(event: WheelEvent, container: HTMLElement): void {
    if (!this.mobileFullscreenOnScrollActive || event.ctrlKey) {
      return;
    }

    if (Math.abs(event.deltaY) < Math.abs(event.deltaX)) {
      return;
    }

    const scrollDownDelta = this.normalizeWheelDeltaY(event);
    if (scrollDownDelta === 0) {
      return;
    }

    const now = performance.now();
    if (now - this.mobileFullscreenLastWheelAt > WHEEL_GESTURE_IDLE_MS) {
      // New wheel/trackpad gesture — don't carry stall count across separate scrolls.
      this.mobileFullscreenChromeOutStallCount = 0;
      this.clearMobileFullscreenGestureScrollParent();
    }
    this.mobileFullscreenLastWheelAt = now;
    this.scheduleMobileFullscreenWheelIdleCleanup();

    if (!this.mobileFullscreenActive && this.tableContentOverflowsVertically(container)) {
      this.prepareMobileFullscreenHandoff(container);
    }

    this.handleMobileFullscreenScrollStep(event, container, scrollDownDelta);
  }

  onTableTouchMove(event: TouchEvent, container: HTMLElement): void {
    if (!this.mobileFullscreenOnScrollActive || event.touches.length !== 1) {
      return;
    }

    const touch = event.touches[0];
    const touchDeltaX = touch.clientX - this.touchStartX;
    const touchDeltaY = touch.clientY - this.touchStartY;
    const touchStepY = touch.clientY - this.touchLastY;
    const deltaX = Math.abs(touchDeltaX);
    const deltaY = Math.abs(touchDeltaY);
    this.touchLastY = touch.clientY;

    if (
      !this.fullscreenGestureAxis &&
      Math.max(deltaX, deltaY) > FULLSCREEN_GESTURE_AXIS_THRESHOLD
    ) {
      this.fullscreenGestureAxis = deltaX > deltaY ? 'horizontal' : 'vertical';
    }

    if (this.fullscreenGestureAxis !== 'vertical') {
      return;
    }

    if (!this.mobileFullscreenActive && this.tableContentOverflowsVertically(container)) {
      this.prepareMobileFullscreenHandoff(container);
    }

    this.handleMobileFullscreenScrollStep(event, container, -touchStepY);
  }

  onTableTouchEnd(_event: TouchEvent, _container: HTMLElement): void {
    if (!this.mobileFullscreenActive) {
      this.clearMobileFullscreenHandoffIfAtRest();
    }

    this.mobileFullscreenChromeOutStallCount = 0;
    this.fullscreenGestureAxis = null;
    this.clearMobileFullscreenGestureScrollParent();
  }

  /** Positive scrollDownDelta scrolls content down / into fullscreen; negative reverses. */
  private handleMobileFullscreenScrollStep(
    event: Event,
    container: HTMLElement,
    scrollDownDelta: number
  ): boolean {
    if (!this.mobileFullscreenOnScrollActive || scrollDownDelta === 0) {
      return false;
    }

    if (scrollDownDelta > 0 && !this.mobileFullscreenActive) {
      if (!this.tableContentOverflowsVertically(container)) {
        if (!this.shouldEnterMobileFullscreen(container)) {
          return false;
        }

        // Keep handoff padding until gesture end (touchend / wheel idle); clearing it
        // mid-gesture clamps parent scrollTop and looks like a jump to the top.
        return this.scrollPageBeforeTableOverflow(event, container, scrollDownDelta);
      }
      return this.scrollPageChromeOut(event, container, scrollDownDelta);
    }

    if (scrollDownDelta > 0 && this.mobileFullscreenActive) {
      this.scrollTableContainer(container, scrollDownDelta);
      this.preventMobileFullscreenDefault(event);
      return true;
    }

    if (scrollDownDelta < 0 && this.mobileFullscreenActive && container.scrollTop > 0) {
      this.scrollTableContainer(container, scrollDownDelta);
      this.preventMobileFullscreenDefault(event);
      return true;
    }

    if (
      scrollDownDelta < 0 &&
      (this.mobileFullscreenActive || this.mobileFullscreenHandoffActive) &&
      container.scrollTop <= 0
    ) {
      if (this.mobileFullscreenActive) {
        this.prepareMobileFullscreenReverseHandoff();
        this.setMobileFullscreenActive(false);
        this.cdr.detectChanges();
      }
      return this.scrollPageChromeIn(event, container, -scrollDownDelta);
    }

    return false;
  }

  private scrollPageChromeOut(
    event: Event,
    container: HTMLElement,
    requestedScroll: number
  ): boolean {
    if (!this.tableContentOverflowsVertically(container)) {
      return this.scrollPageBeforeTableOverflow(event, container, requestedScroll);
    }

    // If native scroll already moved the table (common once touchmoves become
    // non-cancelable), adopt fullscreen instead of yanking scrollTop back to 0.
    if (container.scrollTop > FULLSCREEN_SCROLL_EPSILON) {
      return this.enterMobileFullscreenAndScrollTable(event, container, requestedScroll);
    }

    this.prepareMobileFullscreenHandoff(container);

    let topOffset = this.getMobileFullscreenTopOffsetPx();
    let remainingChromeHeight = Math.max(container.getBoundingClientRect().top - topOffset, 0);
    if (remainingChromeHeight <= FULLSCREEN_SCROLL_EPSILON) {
      this.refreshMobileFullscreenTopOffset();
      topOffset = this.getMobileFullscreenTopOffsetPx();
      remainingChromeHeight = Math.max(container.getBoundingClientRect().top - topOffset, 0);
    }

    if (remainingChromeHeight <= FULLSCREEN_SCROLL_EPSILON) {
      if (!this.shouldEnterMobileFullscreen(container)) {
        this.preventMobileFullscreenDefault(event);
        return true;
      }
      return this.enterMobileFullscreenAndScrollTable(event, container, requestedScroll);
    }

    const scrollAmount = Math.min(requestedScroll, remainingChromeHeight);
    const tableTopBeforeScroll = container.getBoundingClientRect().top;
    let scrolled = this.scrollMobileFullscreenParent(scrollAmount);
    if (!scrolled) {
      // Handoff padding may not have updated scrollHeight yet in this frame.
      void this.hostElement.nativeElement.offsetHeight;
      scrolled = this.scrollMobileFullscreenParent(scrollAmount);
    }
    if (!scrolled) {
      return this.handleChromeOutNoProgress(
        event,
        container,
        requestedScroll,
        remainingChromeHeight
      );
    }

    this.refreshMobileFullscreenTopOffset({ shrinkOnly: true });
    this.recalculateFitToViewport();
    this.preventMobileFullscreenDefault(event);
    container.scrollTop = 0;

    const tableTopAfterScroll = container.getBoundingClientRect().top;
    const tableTopDelta = tableTopBeforeScroll - tableTopAfterScroll;
    topOffset = this.getMobileFullscreenTopOffsetPx();
    remainingChromeHeight = Math.max(tableTopAfterScroll - topOffset, 0);

    if (tableTopDelta < FULLSCREEN_SCROLL_EPSILON) {
      return this.handleChromeOutNoProgress(
        event,
        container,
        requestedScroll,
        remainingChromeHeight
      );
    }

    this.mobileFullscreenChromeOutStallCount = 0;

    return this.finishChromeOutIfAtTop(
      event,
      container,
      Math.max(requestedScroll - scrollAmount, 0)
    );
  }

  private handleChromeOutNoProgress(
    event: Event,
    container: HTMLElement,
    requestedScroll: number,
    remainingChromeHeight: number
  ): boolean {
    this.mobileFullscreenChromeOutStallCount += 1;
    // First skipped parent scrolls are often layout lag after handoff padding.
    // Enter fullscreen when chrome is gone, or only after a sustained stall while
    // chrome remains (avoid jumping into fullscreen on two consecutive lag frames).
    const chromeGone = remainingChromeHeight <= FULLSCREEN_SCROLL_EPSILON;
    const stalledTooLong =
      this.mobileFullscreenChromeOutStallCount >= FULLSCREEN_CHROME_OUT_STALL_FORCE;
    if ((chromeGone || stalledTooLong) && this.shouldEnterMobileFullscreen(container)) {
      return this.enterMobileFullscreenAndScrollTable(event, container, requestedScroll);
    }

    this.preventMobileFullscreenDefault(event);
    return true;
  }

  private enterMobileFullscreenAndScrollTable(
    event: Event,
    container: HTMLElement,
    requestedScroll: number
  ): boolean {
    this.setMobileFullscreenActive(true);
    this.cdr.detectChanges();
    this.scrollTableContainer(container, requestedScroll);
    this.preventMobileFullscreenDefault(event);
    return true;
  }

  private scrollPageBeforeTableOverflow(
    event: Event,
    container: HTMLElement,
    requestedScroll: number
  ): boolean {
    const scrolled = this.scrollMobileFullscreenParent(requestedScroll);
    if (!scrolled) {
      // Parent cannot move further. Enter fullscreen when this gesture started on an
      // overflowing table (sticky headers need the table to be the scroll container).
      if (this.shouldEnterMobileFullscreen(container)) {
        return this.enterMobileFullscreenAndScrollTable(event, container, requestedScroll);
      }
      return false;
    }

    this.recalculateFitToViewport();
    if (
      this.preventMobileFullscreenDefault(event) ||
      container.scrollTop <= FULLSCREEN_SCROLL_EPSILON
    ) {
      container.scrollTop = 0;
    }

    return this.finishChromeOutIfAtTop(event, container, 0);
  }

  private finishChromeOutIfAtTop(
    event: Event,
    container: HTMLElement,
    requestedScroll: number
  ): boolean {
    const topOffset = this.getMobileFullscreenTopOffsetPx();
    const remainingChromeHeight = Math.max(container.getBoundingClientRect().top - topOffset, 0);
    if (remainingChromeHeight > FULLSCREEN_SCROLL_EPSILON) {
      return true;
    }

    if (!this.shouldEnterMobileFullscreen(container)) {
      return true;
    }

    return this.enterMobileFullscreenAndScrollTable(event, container, requestedScroll);
  }

  /** Fullscreen only helps when rows already overflow the visible table area. */
  private tableContentOverflowsVertically(container: HTMLElement): boolean {
    return container.scrollHeight > container.clientHeight + FULLSCREEN_SCROLL_EPSILON;
  }

  /**
   * Enter fullscreen when the table still overflows, would overflow in fullscreen,
   * or overflowed when handoff started (grow-only fit may have temporarily cleared it).
   */
  private shouldEnterMobileFullscreen(container: HTMLElement): boolean {
    return (
      this.mobileFullscreenHandoffHadOverflow ||
      this.tableContentOverflowsVertically(container) ||
      container.scrollHeight >
        this.getMobileFullscreenTableMaxHeightPx() + FULLSCREEN_SCROLL_EPSILON
    );
  }

  private getMobileFullscreenTableMaxHeightPx(): number {
    const viewportHeight =
      this.getMobileFullscreenViewportHeightStylePx() ??
      window.visualViewport?.height ??
      window.innerHeight;
    const topOffset = this.getMobileFullscreenTopOffsetPx();
    const bottomOffset = this.paginationEnabled ? 57 : 2;
    return Math.max(viewportHeight - topOffset - bottomOffset, 0);
  }

  private scrollPageChromeIn(
    event: Event,
    container: HTMLElement,
    requestedScroll: number
  ): boolean {
    const scrolled = this.scrollMobileFullscreenParent(-requestedScroll);
    if (!scrolled) {
      // Parent is at rest. Clear handoff so wheel/trackpad (no touchend) can resume
      // normal scrolling and fit-to-viewport; safe because we are not mid-scroll.
      this.clearMobileFullscreenHandoffIfAtRest();
      if (!this.mobileFullscreenHandoffActive && !this.mobileFullscreenActive) {
        return false;
      }
      this.preventMobileFullscreenDefault(event);
      return true;
    }

    // Do not shrink/unset fit-to-viewport here: collapsing max-height mid-gesture
    // clamps parent scrollTop to 0 and feels like an instant jump to the top.
    // Fit/handoff restore on touchend or wheel idle when the parent is at rest.
    container.scrollTop = 0;
    this.preventMobileFullscreenDefault(event);

    return true;
  }

  private recalculateFitToViewport(options: { growOnly?: boolean } = {}): void {
    if (!this.fitToViewport || this.mobileFullscreenActive) {
      return;
    }

    const handoffActive = this.mobileFullscreenHandoffActive;
    const growOnly = options.growOnly ?? handoffActive;
    const maxHeightCap = handoffActive ? this.getMobileFullscreenTableMaxHeightPx() : undefined;
    this.fitToViewportDirective?.calculateAndSetMaxHeight({
      growOnly,
      force: handoffActive,
      maxHeightCap,
    });
  }

  private normalizeWheelDeltaY(event: WheelEvent): number {
    if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) {
      return event.deltaY * WHEEL_LINE_HEIGHT_PX;
    }
    if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) {
      return event.deltaY * window.innerHeight;
    }
    return event.deltaY;
  }

  private prepareMobileFullscreenHandoff(container: HTMLElement): void {
    if (
      !this.mobileFullscreenOnScrollActive ||
      this.mobileFullscreenActive ||
      !this.tableContentOverflowsVertically(container)
    ) {
      return;
    }

    this.captureMobileFullscreenTopOffset();
    const topOffset = this.getMobileFullscreenTopOffsetPx();
    const scrollParent = this.getMobileFullscreenGestureScrollParent();
    const scrollParentRect = scrollParent?.getBoundingClientRect();
    const hostRect = this.hostElement.nativeElement.getBoundingClientRect();
    const bottomGap = scrollParentRect ? Math.max(scrollParentRect.bottom - hostRect.bottom, 0) : 0;
    const scrollRoom = Math.max(
      container.getBoundingClientRect().top - topOffset + bottomGap + FULLSCREEN_SCROLL_ROOM_BUFFER,
      0
    );
    const nextScrollRoom = Math.max(this.mobileFullscreenScrollRoom, Math.ceil(scrollRoom));
    if (this.mobileFullscreenHandoffActive && this.mobileFullscreenScrollRoom === nextScrollRoom) {
      return;
    }

    this.mobileFullscreenHandoffActive = nextScrollRoom > 0;
    this.mobileFullscreenScrollRoom = nextScrollRoom;
    // Grow-only fit can clear current overflow mid-gesture; remember if we ever had it.
    this.mobileFullscreenHandoffHadOverflow =
      this.mobileFullscreenHandoffHadOverflow || this.tableContentOverflowsVertically(container);
    this.cdr.detectChanges();
    // Ensure handoff padding contributes to scrollHeight before the same gesture scrolls.
    void this.hostElement.nativeElement.offsetHeight;
  }

  private prepareMobileFullscreenReverseHandoff(): void {
    this.captureMobileFullscreenTopOffset();
    const scrollParent = this.getMobileFullscreenGestureScrollParent();
    if (!scrollParent) {
      return;
    }

    const reverseScrollRoom =
      scrollParent.scrollTop +
      this.getMobileFullscreenTopOffsetPx() * 2 +
      FULLSCREEN_SCROLL_ROOM_BUFFER;
    const nextScrollRoom = Math.max(this.mobileFullscreenScrollRoom, Math.ceil(reverseScrollRoom));
    if (this.mobileFullscreenHandoffActive && this.mobileFullscreenScrollRoom === nextScrollRoom) {
      return;
    }

    this.mobileFullscreenHandoffActive = nextScrollRoom > 0;
    this.mobileFullscreenScrollRoom = nextScrollRoom;
    this.cdr.detectChanges();
  }

  private clearMobileFullscreenHandoffIfAtRest(): void {
    const scrollParent = this.getMobileFullscreenGestureScrollParent();
    if (scrollParent && scrollParent.scrollTop > 0) {
      return;
    }

    this.clearMobileFullscreenHandoff();
  }

  /** Wheel/trackpad equivalent of touchend: reset stalls and clear handoff at rest. */
  private scheduleMobileFullscreenWheelIdleCleanup(): void {
    this.clearMobileFullscreenWheelIdleTimer();
    this.mobileFullscreenWheelIdleTimer = setTimeout(() => {
      this.mobileFullscreenWheelIdleTimer = undefined;
      this.mobileFullscreenChromeOutStallCount = 0;
      if (!this.mobileFullscreenActive) {
        this.clearMobileFullscreenHandoffIfAtRest();
      }
      this.clearMobileFullscreenGestureScrollParent();
    }, WHEEL_GESTURE_IDLE_MS);
  }

  private clearMobileFullscreenWheelIdleTimer(): void {
    if (this.mobileFullscreenWheelIdleTimer == null) {
      return;
    }
    clearTimeout(this.mobileFullscreenWheelIdleTimer);
    this.mobileFullscreenWheelIdleTimer = undefined;
  }

  private clearMobileFullscreenHandoff(): void {
    this.clearMobileFullscreenGestureScrollParent();
    if (
      !this.mobileFullscreenHandoffActive &&
      this.mobileFullscreenScrollRoom === 0 &&
      !this.mobileFullscreenHandoffHadOverflow
    ) {
      return;
    }

    this.mobileFullscreenHandoffActive = false;
    this.mobileFullscreenScrollRoom = 0;
    this.mobileFullscreenHandoffHadOverflow = false;
    this.releaseMobileFullscreenTopOffsetIfInactive();
    this.cdr.markForCheck();
    this.scheduleFitToViewportRecalculation();
  }

  private scheduleMobileFullscreenHandoffOverflowRevalidation(): void {
    if (
      !this.viewInitialized ||
      !this.mobileFullscreenHandoffActive ||
      this.mobileFullscreenActive ||
      this.mobileFullscreenHandoffOverflowRevalidationFrame !== undefined
    ) {
      return;
    }

    this.mobileFullscreenHandoffOverflowRevalidationFrame = requestAnimationFrame(() => {
      this.mobileFullscreenHandoffOverflowRevalidationFrame = undefined;
      const container = this.tableContainer?.nativeElement;
      if (!container || !this.mobileFullscreenHandoffActive || this.mobileFullscreenActive) {
        return;
      }

      const needsFullscreen =
        this.tableContentOverflowsVertically(container) ||
        container.scrollHeight >
          this.getMobileFullscreenTableMaxHeightPx() + FULLSCREEN_SCROLL_EPSILON;
      if (this.mobileFullscreenHandoffHadOverflow === needsFullscreen) {
        return;
      }

      this.mobileFullscreenHandoffHadOverflow = needsFullscreen;
    });
  }

  private cancelMobileFullscreenHandoffOverflowRevalidation(): void {
    if (this.mobileFullscreenHandoffOverflowRevalidationFrame === undefined) {
      return;
    }

    cancelAnimationFrame(this.mobileFullscreenHandoffOverflowRevalidationFrame);
    this.mobileFullscreenHandoffOverflowRevalidationFrame = undefined;
  }

  private get mobileFullscreenOnScrollActive(): boolean {
    return this.mobileFullscreenOnScroll && this.mobileTableActive;
  }

  private setMobileFullscreenActive(active: boolean): void {
    const nextActive = active && this.mobileFullscreenOnScrollActive;
    if (this.mobileFullscreenActive === nextActive) {
      return;
    }

    this.mobileFullscreenActive = nextActive;
    if (nextActive) {
      this.captureMobileFullscreenTopOffset();
      this.mobileFullscreenHandoffActive = false;
      this.mobileFullscreenScrollRoom = 0;
      this.mobileFullscreenHandoffHadOverflow = false;
      this.scheduleMobileFullscreenActiveTopOffsetRefresh();
    } else {
      this.cancelMobileFullscreenActiveTopOffsetRefresh();
      this.releaseMobileFullscreenTopOffsetIfInactive();
    }
    this.cdr.markForCheck();
    this.scheduleFitToViewportRecalculation();
  }

  private scheduleFitToViewportRecalculation(): void {
    if (!this.fitToViewport) {
      return;
    }

    requestAnimationFrame(() => {
      this.fitToViewportDirective?.calculateAndSetMaxHeight();
    });
  }

  private scrollMobileFullscreenParent(deltaY: number): boolean {
    const scrollParent = this.getMobileFullscreenGestureScrollParent();
    if (!scrollParent) {
      return false;
    }

    const currentScrollTop = scrollParent.scrollTop;
    const maxScrollTop = scrollParent.scrollHeight - scrollParent.clientHeight;
    const nextScrollTop = Math.min(Math.max(currentScrollTop + deltaY, 0), maxScrollTop);
    if (nextScrollTop === currentScrollTop) {
      return false;
    }

    scrollParent.scrollTop = nextScrollTop;
    return true;
  }

  private scrollTableContainer(container: HTMLElement, deltaY: number): void {
    if (Math.abs(deltaY) <= FULLSCREEN_SCROLL_EPSILON) {
      return;
    }

    this.scheduleMobileFullscreenActiveTopOffsetRefresh();

    const currentScrollTop = container.scrollTop;
    const maxScrollTop = container.scrollHeight - container.clientHeight;
    const nextScrollTop = Math.min(Math.max(currentScrollTop + deltaY, 0), maxScrollTop);
    if (nextScrollTop === currentScrollTop) {
      return;
    }

    container.scrollTop = nextScrollTop;
    this.onTableScroll(container);
  }

  private getMobileFullscreenScrollParent(): HTMLElement | null {
    const topOffset = this.getMobileFullscreenTopOffsetPx();
    const scrollableAncestors: HTMLElement[] = [];
    let element = this.hostElement.nativeElement.parentElement;

    while (element && element !== document.body) {
      const overflowY = getComputedStyle(element).overflowY;
      if (/(auto|scroll|overlay)/.test(overflowY)) {
        scrollableAncestors.push(element);
      }
      element = element.parentElement;
    }

    const pinnedAncestors = scrollableAncestors.filter(
      (ancestor) =>
        ancestor.clientHeight > 0 &&
        ancestor.getBoundingClientRect().top <= topOffset + FULLSCREEN_SCROLL_EPSILON
    );

    return (
      pinnedAncestors.find((ancestor) => this.hasScrollableOverflow(ancestor)) ??
      pinnedAncestors.at(0) ??
      scrollableAncestors.find((ancestor) => this.hasScrollableOverflow(ancestor)) ??
      scrollableAncestors.at(-1) ??
      (document.scrollingElement as HTMLElement | null)
    );
  }

  private getMobileFullscreenGestureScrollParent(): HTMLElement | null {
    const cachedScrollParent = this.mobileFullscreenGestureScrollParent;
    if (cachedScrollParent === null || cachedScrollParent?.isConnected) {
      return cachedScrollParent;
    }

    const scrollParent = this.getMobileFullscreenScrollParent();
    this.mobileFullscreenGestureScrollParent = scrollParent;
    return scrollParent;
  }

  private clearMobileFullscreenGestureScrollParent(): void {
    this.mobileFullscreenGestureScrollParent = undefined;
  }

  private hasScrollableOverflow(element: HTMLElement): boolean {
    return element.scrollHeight - element.clientHeight > FULLSCREEN_SCROLL_EPSILON;
  }

  private getMobileFullscreenTopOffsetPx(): number {
    if (
      (this.mobileFullscreenActive || this.mobileFullscreenHandoffActive) &&
      this.mobileFullscreenTopOffsetPx !== undefined
    ) {
      return this.mobileFullscreenTopOffsetPx;
    }

    return this.getMobileFullscreenTopOffsetStylePx() ?? this.parseMobileFullscreenTopOffsetPx();
  }

  private getMobileFullscreenTopOffsetStylePx(): number | undefined {
    if (
      (this.mobileFullscreenActive || this.mobileFullscreenHandoffActive) &&
      this.mobileFullscreenTopOffsetPx !== undefined
    ) {
      return this.mobileFullscreenTopOffsetPx;
    }

    return this.mobileFullscreenMeasuredTopOffsetPx;
  }

  private getMobileFullscreenViewportHeightStylePx(): number | undefined {
    if (
      (this.mobileFullscreenActive || this.mobileFullscreenHandoffActive) &&
      this.mobileFullscreenViewportHeightPx !== undefined
    ) {
      return this.mobileFullscreenViewportHeightPx;
    }

    return this.mobileFullscreenMeasuredViewportHeightPx;
  }

  private calculateMobileFullscreenTopOffsetPx(): number {
    const boundary = this.mobileFullscreenTopBoundary;
    if (boundary?.isConnected) {
      return Math.max(0, boundary.getBoundingClientRect().bottom);
    }

    return this.parseMobileFullscreenTopOffsetPx();
  }

  private calculateMobileFullscreenViewportHeightPx(): number {
    return Math.max(window.visualViewport?.height ?? window.innerHeight, 0);
  }

  private captureMobileFullscreenTopOffset(): void {
    if (this.mobileFullscreenTopOffsetPx === undefined) {
      this.mobileFullscreenTopOffsetPx = this.calculateMobileFullscreenTopOffsetPx();
    }

    if (this.mobileFullscreenViewportHeightPx === undefined) {
      this.mobileFullscreenViewportHeightPx = this.calculateMobileFullscreenViewportHeightPx();
    }
  }

  private refreshMobileFullscreenTopOffset(options: { shrinkOnly?: boolean } = {}): boolean {
    const nextTopOffset = this.calculateMobileFullscreenTopOffsetPx();
    const nextViewportHeight = this.calculateMobileFullscreenViewportHeightPx();
    const refreshedTopOffset =
      options.shrinkOnly && this.mobileFullscreenTopOffsetPx !== undefined
        ? Math.min(this.mobileFullscreenTopOffsetPx, nextTopOffset)
        : nextTopOffset;

    if (
      this.mobileFullscreenTopOffsetPx === refreshedTopOffset &&
      this.mobileFullscreenViewportHeightPx === nextViewportHeight
    ) {
      return false;
    }

    this.mobileFullscreenTopOffsetPx = refreshedTopOffset;
    this.mobileFullscreenViewportHeightPx = nextViewportHeight;
    this.cdr.markForCheck();
    return true;
  }

  private releaseMobileFullscreenTopOffsetIfInactive(): void {
    if (this.mobileFullscreenActive || this.mobileFullscreenHandoffActive) {
      return;
    }

    this.mobileFullscreenTopOffsetPx = undefined;
    this.mobileFullscreenViewportHeightPx = undefined;
  }

  private parseMobileFullscreenTopOffsetPx(): number {
    const value = this.mobileFullscreenTopOffset.trim();
    const numericValue = Number.parseFloat(value);
    if (!Number.isFinite(numericValue)) {
      return 0;
    }

    if (value.endsWith('rem')) {
      const rootFontSize = Number.parseFloat(getComputedStyle(document.documentElement).fontSize);
      return numericValue * rootFontSize;
    }

    return numericValue;
  }

  private syncMobileFullscreenTopBoundaryObservers(): void {
    this.teardownMobileFullscreenTopBoundaryObservers();
    if (!this.mobileFullscreenOnScrollActive || !this.mobileFullscreenTopBoundary?.isConnected) {
      return;
    }

    const boundary = this.mobileFullscreenTopBoundary;
    const onBoundaryChanged = () => this.onMobileFullscreenTopBoundaryChanged();
    this.mobileFullscreenTopBoundaryObserver = new ResizeObserver(onBoundaryChanged);
    this.mobileFullscreenTopBoundaryObserver.observe(boundary);

    window.addEventListener('resize', onBoundaryChanged);
    window.visualViewport?.addEventListener('resize', onBoundaryChanged);
    window.visualViewport?.addEventListener('scroll', onBoundaryChanged);
    this.mobileFullscreenTopBoundaryViewportCleanup = () => {
      window.removeEventListener('resize', onBoundaryChanged);
      window.visualViewport?.removeEventListener('resize', onBoundaryChanged);
      window.visualViewport?.removeEventListener('scroll', onBoundaryChanged);
    };

    this.scheduleMobileFullscreenTopBoundaryMeasurement();
  }

  private teardownMobileFullscreenTopBoundaryObservers(): void {
    this.mobileFullscreenTopBoundaryObserver?.disconnect();
    this.mobileFullscreenTopBoundaryObserver = undefined;
    this.mobileFullscreenTopBoundaryViewportCleanup?.();
    this.mobileFullscreenTopBoundaryViewportCleanup = undefined;
    this.cancelMobileFullscreenTopBoundaryMeasurement();
    this.mobileFullscreenMeasuredTopOffsetPx = undefined;
    this.mobileFullscreenMeasuredViewportHeightPx = undefined;
  }

  private onMobileFullscreenTopBoundaryChanged(): void {
    if (this.mobileFullscreenActive || this.mobileFullscreenHandoffActive) {
      this.scheduleMobileFullscreenActiveTopOffsetRefresh();
      return;
    }

    this.scheduleMobileFullscreenTopBoundaryMeasurement();
  }

  private scheduleMobileFullscreenTopBoundaryMeasurement(): void {
    if (this.mobileFullscreenTopBoundaryMeasureFrame !== undefined) {
      return;
    }

    this.mobileFullscreenTopBoundaryMeasureFrame = requestAnimationFrame(() => {
      this.mobileFullscreenTopBoundaryMeasureFrame = undefined;
      if (!this.mobileFullscreenOnScrollActive) {
        return;
      }

      const nextTopOffset = this.calculateMobileFullscreenTopOffsetPx();
      const nextViewportHeight = this.calculateMobileFullscreenViewportHeightPx();
      if (
        this.mobileFullscreenMeasuredTopOffsetPx === nextTopOffset &&
        this.mobileFullscreenMeasuredViewportHeightPx === nextViewportHeight
      ) {
        return;
      }

      this.mobileFullscreenMeasuredTopOffsetPx = nextTopOffset;
      this.mobileFullscreenMeasuredViewportHeightPx = nextViewportHeight;
      this.cdr.markForCheck();
    });
  }

  private cancelMobileFullscreenTopBoundaryMeasurement(): void {
    if (this.mobileFullscreenTopBoundaryMeasureFrame === undefined) {
      return;
    }

    cancelAnimationFrame(this.mobileFullscreenTopBoundaryMeasureFrame);
    this.mobileFullscreenTopBoundaryMeasureFrame = undefined;
  }

  private scheduleMobileFullscreenActiveTopOffsetRefresh(): void {
    if (this.mobileFullscreenTopOffsetRefreshFrame !== undefined) {
      return;
    }

    this.mobileFullscreenTopOffsetRefreshFrame = requestAnimationFrame(() => {
      this.mobileFullscreenTopOffsetRefreshFrame = undefined;
      this.refreshMobileFullscreenActiveTopOffset();
    });
  }

  private cancelMobileFullscreenActiveTopOffsetRefresh(): void {
    if (this.mobileFullscreenTopOffsetRefreshFrame === undefined) {
      return;
    }

    cancelAnimationFrame(this.mobileFullscreenTopOffsetRefreshFrame);
    this.mobileFullscreenTopOffsetRefreshFrame = undefined;
  }

  private refreshMobileFullscreenActiveTopOffset(): boolean {
    if (!this.mobileFullscreenActive && !this.mobileFullscreenHandoffActive) {
      return false;
    }

    return this.refreshMobileFullscreenTopOffset({ shrinkOnly: true });
  }

  private preventMobileFullscreenDefault(event: Event): boolean {
    if (!event.cancelable) {
      return false;
    }

    event.preventDefault();
    return true;
  }

  private setSortAndPagination(): void {
    if (this.sortingEnabled && this.dataSource && this.dataSource.sort) {
      this.dataSource.sortingDataAccessor = (row, key) =>
        this.mainTableCustomSortingService.getCustomSorting(row, key);
      this.dataSource.sort = this.sort;
    }
  }

  private syncSearchControl(searchTerm: string): void {
    if (!this.searchingEnabled || (this.searchControl.value || '') === searchTerm) {
      return;
    }
    this.searchControl.setValue(searchTerm, { emitEvent: false });
    this.lastEmittedSearchTerm = searchTerm;
  }

  private replaceColumnFilters(searchBy: MainTableFilterSearchByModel[] | undefined): void {
    const nextFilters = this.groupFiltersByColumn(searchBy);
    if (JSON.stringify(this.columnFilters) === JSON.stringify(nextFilters)) {
      return;
    }
    this.columnFilters = nextFilters;
  }

  private syncSortHeader(currentFilter: MainTableFilterModel | undefined): void {
    if (!this.sort || !this.sortingEnabled || !this.viewInitialized) {
      return;
    }
    const isThisTable = currentFilter?.module === this.moduleNameForPaging;
    const orderBy = isThisTable ? currentFilter?.orderBy || '' : '';
    const ascDesc = isThisTable ? currentFilter?.ascDesc || '' : '';
    const uiColumnName = orderBy ? this.findUiColumnNameForApiName(orderBy) || '' : '';
    const direction = ascDesc ? this.convertToMatSortDirection(ascDesc) : '';
    if (this.sort.active === uiColumnName && this.sort.direction === direction) {
      return;
    }
    this.isApplyingSavedSort = true;
    this.sort.active = uiColumnName;
    this.sort.direction = direction;
    this.sort.sortChange.emit({ active: uiColumnName, direction });
    this.isApplyingSavedSort = false;
  }

  private applySavedSorting(): void {
    if (!this.sort || !this.sortingEnabled) {
      return;
    }

    const currentFilter = this.store.selectSnapshot(MainTableFilterState.getCurrentFilterDetails);
    if (currentFilter?.module !== this.moduleNameForPaging) {
      return;
    }

    if (!currentFilter?.orderBy || !currentFilter?.ascDesc) {
      return;
    }

    const matSortDirection = this.convertToMatSortDirection(currentFilter.ascDesc);
    const uiColumnName = this.findUiColumnNameForApiName(currentFilter.orderBy);
    if (!uiColumnName) {
      return;
    }
    // Set flag to prevent duplicate API request
    this.isApplyingSavedSort = true;
    this.sort.active = uiColumnName;
    this.sort.direction = matSortDirection;
    this.sort.sortChange.emit({
      active: uiColumnName,
      direction: matSortDirection,
    });
    // Reset flag after emission
    this.isApplyingSavedSort = false;
  }

  private findUiColumnNameForApiName(apiName: string): string | null {
    const foundColumn = this.displayedColumns?.find((column) => {
      const mappedApiName = this.getProperHeaderNameForAPI(column, this.moduleNameForPaging);
      return mappedApiName === apiName;
    });

    return foundColumn || null;
  }

  private convertToMatSortDirection(sortingType: MainTableSortingType): 'asc' | 'desc' | '' {
    if (sortingType === 'Ascending') {
      return 'asc';
    }
    if (sortingType === 'Descending') {
      return 'desc';
    }
    return '';
  }

  private setCustomFilterPredicate() {
    if (this.filterPredicate) {
      this.dataSource.filterPredicate = this.filterPredicate;
    }
  }

  checkboxLabel(row?: any): string {
    if (!row) {
      return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
    }
    const action = this.multipleSelection.isSelected(row) ? 'deselect' : 'select';
    const position = Number(row.position);
    if (!Number.isNaN(position)) {
      return `${action} row ${position + 1}`;
    }

    const rowLabel = row.name || row.title || row.productName || row.articleNo || 'row';
    return `${action} ${rowLabel}`;
  }

  isAllSelected() {
    const numSelected = this.multipleSelection.selected.length;
    const numRows = this.dataSource.data.filter((r) => !r[this.disableSelectionByColumn]).length;
    return numSelected === numRows;
  }

  isAnyItemSelected(): boolean {
    return this.multipleSelection.selected.length > 0;
  }

  toggleAllRows() {
    if (this.isAllSelected()) {
      this.multipleSelection.clear();
    } else {
      this.multipleSelection.select(
        ...this.dataSource.data.filter((r) => !r[this.disableSelectionByColumn])
      );
    }

    this.emitSelection();
  }

  toggleSingle(row: any) {
    const { isSubHeader } = row;
    if (isSubHeader) {
      const groupRows = this.dataSource.data.filter((r) =>
        row.itemIds.includes(r[this.idFieldNameForRowHandler])
      );
      if (this.isAllRowsInGroupSelected(row)) {
        this.multipleSelection.deselect(row, ...groupRows);
      } else {
        this.multipleSelection.select(row, ...groupRows);
      }
      this.emitSelection();
      return;
    }
    this.multipleSelection.toggle(row);
    const rowGroupHeader = this.dataSource.data.find(
      (r) => r.isSubHeader && r.itemIds?.includes(row[this.idFieldNameForRowHandler])
    );
    if (rowGroupHeader) {
      if (this.isAllRowsInGroupSelected(rowGroupHeader)) {
        this.multipleSelection.select(rowGroupHeader);
      } else {
        this.multipleSelection.deselect(rowGroupHeader);
      }
    }
    this.emitSelection();
  }

  clearSelection() {
    this.multipleSelection.clear();
    this.emitSelection();
  }

  isAllRowsInGroupSelected(row: any): boolean {
    const { isSubHeader } = row;
    if (isSubHeader) {
      const rowsToSelect = this.dataSource.data.filter((r) =>
        row.itemIds.includes(r[this.idFieldNameForRowHandler])
      );
      return rowsToSelect.every((r) => this.multipleSelection.isSelected(r));
    }
    return false;
  }

  isSomeRowsInGroupSelected(row: any): boolean {
    const { isSubHeader } = row;
    if (isSubHeader) {
      const rowsToSelect = this.dataSource.data.filter((r) =>
        row.itemIds.includes(r[this.idFieldNameForRowHandler])
      );
      return rowsToSelect.some((r) => this.multipleSelection.isSelected(r));
    }
    return false;
  }

  private emitSelection(): void {
    this.selectionEmitter.emit(this.multipleSelection.selected.filter((r) => !r.isSubHeader));
  }

  private groupFiltersByColumn(
    filters?: MainTableFilterSearchByModel[]
  ): Record<string, MainTableFilterSearchByModel[]> {
    return (filters ?? []).reduce(
      (columnFilters: Record<string, MainTableFilterSearchByModel[]>, filter) => {
        const columnName = this.findOwningColumnForFilterProperty(filter.property);
        if (!columnFilters[columnName]) {
          columnFilters[columnName] = [];
        }
        columnFilters[columnName].push(filter);
        return columnFilters;
      },
      {}
    );
  }

  private findOwningColumnForFilterProperty(property: string): string {
    if (this.filterConfig?.[property]) {
      return property;
    }

    const matchingColumn = Object.keys(this.filterConfig ?? {}).find((columnName) =>
      this.filterConfig?.[columnName]?.sections?.some((section) => section.property === property)
    );

    return matchingColumn || property;
  }

  getProperHeaderNameForAPI(activeHeader: string, module: string): string {
    switch (activeHeader) {
      case 'arrivalDateTime':
        if (module === MainTableFilterModulesEnum.CurrentPersonnel) {
          return 'Arrival';
        }
        return 'From';
      case 'departureDateTime':
        return 'To';
      case 'issueDate':
        return 'IssueDate';
      case 'expiryDate':
        return 'ExpirationDate';
      case 'currentQuantity':
        return 'Quantity';
      case 'critical':
        if (module === MainTableFilterModulesEnum.MaintenanceJobs) {
          return 'IsCriticalComponent';
        }
        return 'IsCritical';
      case 'chemical':
        return 'IsChemical';
      case 'medicineCategory':
        return 'Category';
      case 'minQuantity':
        return 'MinimumQuantity';
      case 'refrigerated':
        return 'ShouldBeRefrigerated';
      case 'departmentType':
        return 'Type';
      case 'status':
        if (module === MainTableFilterModulesEnum.CurrentPersonnel) {
          return '';
        }
        if (
          module === MainTableFilterModulesEnum.Trips ||
          module === MainTableFilterModulesEnum.UserActivities ||
          module === MainTableFilterModulesEnum.MyUserActivities ||
          module === MainTableFilterModulesEnum.PermitToWork
        ) {
          return 'status';
        }
        return 'IsActive';
      case 'fullName':
        return 'FirstName';
      case 'phoneNumber':
        return 'Mobile';
      case 'productName':
        return 'Name';
      case 'name':
        return 'Name';
      case 'position':
        return 'Position';
      case 'readReceiptSummary':
        return 'ReadReceiptSummarySortValue';
      case 'hasReadCurrentReadReceipt':
        return 'HasReadCurrentReadReceipt';
      case 'pausable':
        return 'Pausable';
      case 'plannedEndDate':
        return 'PlannedEnd';
      case 'totalSumVat':
        return 'TotalPrice';
      case 'employedInDepartment':
        return 'employedIn';
      case 'employedInDepartments':
        return 'employmentDepartment';
      case 'crew':
        return 'crewMembersCount';
      case 'project':
        return 'Project.Name';
      case 'sentAt':
        return 'SentAt';
      case 'sentToNextOfKin':
        return 'SentToNextOfKin';
      case 'smsSegmentCount':
        return 'SmsSegmentCount';
      case 'plannedActivityVoyageName':
        return 'tripName';
      default:
        return activeHeader;
    }
  }
}
